// Clue syntax helpers. Mirrors parseClue in the app's cw_clue_utils.dart: a clue is
// plain text or a run of icons ({symbol}, {c.custom}, emoji), optionally quoted
// ("{👑}") and followed by a modifier (x2, +T, -S).

const EMOJI_RE = /[\u{00A9}\u{00AE}\u{203C}\u{2049}\u{2122}\u{2139}\u{2194}-\u{2199}\u{21A9}-\u{21AA}\u{231A}-\u{231B}\u{2328}\u{23CF}\u{23E9}-\u{23F3}\u{23F8}-\u{23FA}\u{24C2}\u{25AA}-\u{25AB}\u{25B6}\u{25C0}\u{25FB}-\u{25FE}\u{2600}-\u{2604}\u{260E}\u{2611}\u{2614}-\u{2615}\u{2618}\u{261D}\u{2620}\u{2622}-\u{2623}\u{2626}\u{262A}\u{262E}-\u{262F}\u{2638}-\u{263A}\u{2640}\u{2642}\u{2648}-\u{2653}\u{2660}\u{2663}\u{2665}-\u{2666}\u{2668}\u{267B}\u{267F}\u{2692}-\u{2697}\u{2699}\u{269B}-\u{269C}\u{26A0}-\u{26A1}\u{26AA}-\u{26AB}\u{26B0}-\u{26B1}\u{26BD}-\u{26BE}\u{26C4}-\u{26C5}\u{26C8}\u{26CE}-\u{26CF}\u{26D1}\u{26D3}-\u{26D4}\u{26E9}-\u{26EA}\u{26F0}-\u{26F5}\u{26F7}-\u{26FA}\u{26FD}\u{2702}\u{2705}\u{2708}-\u{270D}\u{270F}\u{2712}\u{2714}\u{2716}\u{271D}\u{2721}\u{2728}\u{2733}-\u{2734}\u{2744}\u{2747}\u{274C}\u{274E}\u{2753}-\u{2755}\u{2757}\u{2763}-\u{2764}\u{2795}-\u{2797}\u{27A1}\u{27B0}\u{27BF}\u{2934}-\u{2935}\u{2B05}-\u{2B07}\u{2B1B}-\u{2B1C}\u{2B50}\u{2B55}\u{3030}\u{303D}\u{3297}\u{3299}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F7E0}-\u{1F7EB}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}]/u;
const ICON_TOKEN_RE = /^(?:\{c\.([a-z0-9_]+)\}|\{([a-z_]+)\}|\{([^{}]+)\})/;
const MODIFIER_RE = /^[x+\-].+$/;

const segmenter = typeof Intl !== 'undefined' && Intl.Segmenter ? new Intl.Segmenter('fi', { granularity: 'grapheme' }) : null;
const graphemes = (s) => (segmenter ? [...segmenter.segment(s)].map(x => x.segment) : [...s]);
const isEmoji = (g) => g.includes('⃣') || EMOJI_RE.test(g);

const scanIcons = (clue) => {
  let rest = (clue || '').trim();
  const quoted = rest.startsWith('"');
  if (quoted) rest = rest.slice(1);
  const glyphs = [];
  let closed = !quoted;
  // Only a closing brace or quote can take a modifier, so "👍-merkki" stays text
  let canModify = false;

  while (rest) {
    if (quoted && rest.startsWith('"')) {
      rest = rest.slice(1);
      closed = canModify = true;
      break;
    }
    const token = rest.match(ICON_TOKEN_RE);
    if (token) {
      if (token[1]) glyphs.push({ type: 'custom', content: token[1] });
      else if (token[2]) glyphs.push({ type: 'symbol', content: token[2] });
      else {
        if (!EMOJI_RE.test(token[3])) return null;
        graphemes(token[3]).filter(g => g.trim()).forEach(g => glyphs.push({ type: 'emoji', content: g }));
      }
      rest = rest.slice(token[0].length);
      canModify = true;
      continue;
    }
    const g = graphemes(rest)[0];
    if (isEmoji(g)) {
      glyphs.push({ type: 'emoji', content: g });
      canModify = false;
    } else if (g.trim()) {
      break;
    }
    rest = rest.slice(g.length);
  }
  return { glyphs, quoted, closed, canModify, rest };
};

const parseCache = new Map();

// Returns { type: 'empty' | 'text' | 'icons', content, glyphs, modifier?, quoted? }.
export const parseClue = (clue) => {
  if (!clue || !clue.trim()) return { type: 'empty', content: '', glyphs: [] };
  if (parseCache.has(clue)) return parseCache.get(clue);
  if (parseCache.size > 5000) parseCache.clear();

  const text = { type: 'text', content: clue, glyphs: [] };
  const scan = scanIcons(clue);
  let parsed = text;
  if (scan && scan.glyphs.length && scan.closed) {
    const icons = { type: 'icons', content: clue, glyphs: scan.glyphs, quoted: scan.quoted };
    if (!scan.rest) parsed = icons;
    else if (scan.canModify && MODIFIER_RE.test(scan.rest)) parsed = { ...icons, modifier: scan.rest };
  }
  parseCache.set(clue, parsed);
  return parsed;
};

export const isIconClue = (clue) => parseClue(clue).type === 'icons';

// Splits an icon clue into its icons and trailing modifier text:
// "{cat}x2" -> { base: "{cat}", modifier: "x2", quoted: false }. Works mid-edit ("{cat}+") too.
// Raw emoji get braces so a modifier can follow. Returns null for text clues.
export const splitIconClue = (clue) => {
  const scan = scanIcons(clue);
  if (!scan || !scan.glyphs.length || !scan.closed) return null;
  if (scan.rest && !(scan.canModify && /^[x+\-]/.test(scan.rest))) return null;
  const trimmed = (clue || '').trim();
  let base = trimmed.slice(0, trimmed.length - scan.rest.length).trim();
  if (scan.quoted) base = base.slice(1, -1);
  if (!scan.canModify) base = `{${base}}`;
  return { base, modifier: scan.rest, quoted: scan.quoted };
};

// An emoji with neither quotes nor a modifier is stored bare, matching how icon
// clues are synced (see syncIconCluesFromPuzzle).
const composeIconClue = (base, quoted, modifier) => {
  if (quoted) return `"${base}"${modifier}`;
  const braced = base.endsWith('}') ? base : `{${base}}`;
  if (modifier) return braced + modifier;
  const single = braced.match(/^\{([^{}]+)\}$/);
  return single && !/^(c\.)?[a-z0-9_]+$/.test(single[1]) ? single[1] : braced;
};

// Replaces the modifier of an icon clue.
export const withModifier = (clue, modifier) => {
  const parts = splitIconClue(clue);
  return parts ? composeIconClue(parts.base, parts.quoted, modifier || '') : clue;
};

// Wraps an icon clue in quotes, drawn at the icon's top corners, or removes them.
export const withQuotes = (clue, quoted) => {
  const parts = splitIconClue(clue);
  return parts ? composeIconClue(parts.base, quoted, parts.modifier) : clue;
};

// Modifiers seen in published puzzles (x2 is by far the most common).
export const QUICK_MODIFIERS = [
  { value: 'x2', label: '×2' },
  { value: 'x3', label: '×3' },
];

// Editor clues use ︲ as a visible placeholder for the soft hyphen.
export const toEditorClue = (clue) => (clue || '').replace(/\u00AD/g, '︲');
export const toDisplayClue = (clue) => (clue || '').replace(/[\u00AD︲]/g, '');

// Drops the emoji presentation selector (U+FE0F) for text rendered in Noto Emoji.
// With it, Chrome prefers a color emoji font (Segoe UI Emoji on Windows) over the
// monochrome Noto Emoji, so e.g. ♠️ ❤️ ☀️ fall back to the system look. ZWJ
// sequences are left intact since their ligatures may depend on it.
export const forNotoEmoji = (s) => (s.includes('\u200D') ? s : s.replace(/\uFE0F/g, ''));
